import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { updateUser, clearError } from '../../store/slices/usersSlice';
import { User, UpdateUserRequest } from '../../types/auth';
import { useAuth } from '../../contexts/AuthContext';

interface UserEditDialogProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const UserEditDialog: React.FC<UserEditDialogProps> = ({
  open,
  user,
  onClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const { user: currentUser } = useAuth();
  const { isUpdating, error } = useAppSelector((state) => state.users);

  const [formData, setFormData] = useState<UpdateUserRequest>({
    username: '',
    email: '',
    role: 'user',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open && user) {
      // Populate form with user data
      setFormData({
        username: user.username,
        email: user.email,
        role: user.role,
      });
      setFormErrors({});
      dispatch(clearError());
    }
  }, [open, user, dispatch]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.username?.trim()) {
      errors.username = 'Benutzername ist erforderlich';
    } else if (formData.username.length < 3) {
      errors.username = 'Benutzername muss mindestens 3 Zeichen haben';
    }

    if (!formData.email?.trim()) {
      errors.email = 'E-Mail ist erforderlich';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Ungültige E-Mail-Adresse';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: keyof UpdateUserRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    
    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleRoleChange = (event: any) => {
    setFormData(prev => ({
      ...prev,
      role: event.target.value as 'admin' | 'user' | 'viewer',
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!user || !validateForm()) {
      return;
    }

    try {
      await dispatch(updateUser({ id: user.id, data: formData })).unwrap();
      onSuccess?.();
      onClose();
    } catch (error) {
      // Error is handled by Redux slice
    }
  };

  const handleClose = () => {
    if (!isUpdating) {
      onClose();
    }
  };

  // Check if this is the admin user being edited
  const isAdminUser = user?.role === 'admin' && user?.username === 'admin';
  
  // Check if current user is editing themselves
  const isSelfEdit = currentUser?.id === user?.id;
  
  // Prevent role changes for admin user or if editing yourself from admin to non-admin
  const canChangeRole = !isAdminUser && !(isSelfEdit && currentUser?.role === 'admin');

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Benutzer bearbeiten
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          disabled={isUpdating}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isAdminUser && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Der Admin-Benutzer kann nicht gelöscht oder in der Rolle geändert werden.
            </Alert>
          )}

          {isSelfEdit && currentUser?.role === 'admin' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Sie können Ihre eigene Rolle als Administrator nicht ändern.
            </Alert>
          )}

          <TextField
            fullWidth
            label="Benutzername"
            value={formData.username || ''}
            onChange={handleInputChange('username')}
            error={!!formErrors.username}
            helperText={formErrors.username}
            margin="normal"
            required
            disabled={isUpdating}
          />

          <TextField
            fullWidth
            label="E-Mail"
            type="email"
            value={formData.email || ''}
            onChange={handleInputChange('email')}
            error={!!formErrors.email}
            helperText={formErrors.email}
            margin="normal"
            required
            disabled={isUpdating}
          />

          <FormControl fullWidth margin="normal" required disabled={isUpdating || !canChangeRole}>
            <InputLabel>Rolle</InputLabel>
            <Select
              value={formData.role || 'user'}
              onChange={handleRoleChange}
              label="Rolle"
            >
              <MenuItem value="user">Benutzer</MenuItem>
              <MenuItem value="admin">Administrator</MenuItem>
              <MenuItem value="viewer">Betrachter</MenuItem>
            </Select>
            {!canChangeRole && (
              <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '4px' }}>
                {isAdminUser 
                  ? 'Die Rolle des Admin-Benutzers kann nicht geändert werden'
                  : 'Sie können Ihre eigene Rolle nicht ändern'
                }
              </div>
            )}
          </FormControl>

          <TextField
            fullWidth
            label="Erstellt am"
            value={user ? new Date(user.created_at).toLocaleString('de-DE') : ''}
            margin="normal"
            disabled
            variant="filled"
          />

          <TextField
            fullWidth
            label="Zuletzt aktualisiert"
            value={user ? new Date(user.updated_at).toLocaleString('de-DE') : ''}
            margin="normal"
            disabled
            variant="filled"
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={isUpdating}
            color="secondary"
          >
            Abbrechen
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isUpdating}
            startIcon={isUpdating && <CircularProgress size={20} />}
          >
            {isUpdating ? 'Speichere...' : 'Änderungen speichern'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserEditDialog;